import { getNow, isAfter, getActiveResources, isBeforeOrEqual } from '@kathondvla/sri-client/date-utils';
import {
  DELEGATED_ADMIN,
  DELEGATED_PEDAGOGIC,
  FORBIDDEN,
  GROUP_MANAGER,
  KATHOLIEK_ONDERWIJS_VLAANDEREN,
  MANAGER,
  STRATEGIC_PURCHASER,
  INVOICING_ADMIN,
} from 'ReduxLoop/constants';
import {
  displayOuType,
  OLD_MIJN_OU_TYPE_CONFIG,
  OU_TYPE_CONFIG,
  getOuTypeConfigKey,
  POSSIBLE_MANAGEMENT_TABS,
} from 'ReduxLoop/appConfig';
import { settings } from 'Js/config/settings';
import { mapToArray } from 'ReduxLoop/viewModel';

const getUserResponsibilityInfo = (state, responsibility, ou, role) => {
  let hasAccess = false;
  if (role === GROUP_MANAGER || ou.$$meta.permalink.match(/^\/kathondvla/)) {
    hasAccess = true;
  } else if (ou.type === 'TEACHER_GROUP' && (role === MANAGER || role === DELEGATED_ADMIN)) {
    hasAccess = state.userState.ousWithUpdateAccessToResps.some((ouKey) => ouKey === ou.key);
  } else if (role === MANAGER || role === DELEGATED_ADMIN || role === STRATEGIC_PURCHASER) {
    hasAccess = state.userState.ousWithAccessToInvitations.some((ouKey) => ouKey === ou.key);
  } else if (role === DELEGATED_PEDAGOGIC) {
    hasAccess =
      state.groupsTab.groups.size === 0 ||
      !mapToArray(state.groupsTab.groups.toJS()).some(
        (group) => !state.userState.ousWithReadAccessToResps.some((ouKey) => ouKey === group.key)
      );
  } else if (role === INVOICING_ADMIN) {
    hasAccess = state.userState.ousWithAccessToAccountingDetails.some((ouKey) => ouKey === ou.key);
  }
  return {
    name: ou.$$displayName,
    ouKey: ou.key,
    ouType: ou.type,
    educationLevel: state.userState.educationLevels[ou.$$meta.permalink],
    institutionNumber: state.userState.institutionNumbers[ou.$$meta.permalink],
    ouPermalink: ou.$$meta.permalink,
    permalink: responsibility ? responsibility.$$meta.permalink : null,
    startDate: responsibility ? responsibility.startDate : null,
    endDate: responsibility ? responsibility.endDate : null,
    inFuture:
      responsibility && responsibility.startDate && isAfter(responsibility.startDate, getNow()),
    // locations: [],
    role,
    noAccessYet: !hasAccess,
    canGrantAccessToSensitiveInformation:
      state.userState.ousWithGrantAccessToSensitiveInformation.some((ouKey) => ouKey === ou.key),
    href: `/#!/${displayOuType(ou.type).toLowerCase()}/${ou.key}`,
  };
};

const OU_TYPES_FOR_KWALITEITSPLATFORM_TILE = ['SCHOOL', 'SCHOOLENTITY', 'SCHOOLCOMMUNITY', 'BOARDING', 'GOVERNINGINSTITUTION'];
const EDUCATIONLEVELS_FOR_KWALITEITSPLATFORM_TILE = ['PRIMARY', 'SECONDARY'];
const hasKwaliteitsplatformTile = (responsibilities, ouMap, educationLevelMap) => {
  if (!responsibilities || !ouMap || !educationLevelMap) {
    return false;
  }
  return responsibilities.some((resp) => {
    const ou = ouMap[resp.organisationalUnit.href];
    return ou
      && OU_TYPES_FOR_KWALITEITSPLATFORM_TILE.includes(ou.type) 
      && (!['SCHOOL', 'SCHOOLENTITY'].includes(ou.type) || EDUCATIONLEVELS_FOR_KWALITEITSPLATFORM_TILE.includes(educationLevelMap[ou.$$meta.permalink]));
  });
}

export const getUserInfo = (state) => {
  let userInfo = {};

  if (state.userState.user) {
    const profilePictures = state.userState.user.attachments.filter(
      (att) =>
        att.$$expanded.type === 'PROFILE_IMAGE' && att.$$expanded.name === 'profile-small.jpg'
    );
    userInfo = {
      key: state.userState.user.key,
      firstName: state.userState.user.firstName,
      lastName: state.userState.user.lastName,
      username: state.userState.user.username,
      permalink: state.userState.user.$$meta.permalink,
      imageUrl:
        profilePictures.length > 0
          ? `${settings.apis.kathOndVla + profilePictures[0].$$expanded.href}?${
              profilePictures[0].$$expanded.key
            }`
          : null,
      hasKwaliteitsplatformTile: hasKwaliteitsplatformTile(state.userState.responsibilities, state.userState.ous, state.userState.educationLevels),
      isSuperAdmin: state.userState.isSuperAdmin,
      canDeleteKathOndVlaOus: state.userState.canDeleteKathOndVlaOus,
      adminForOuTypes: state.userState.adminForOuTypes.size > 0
        ? state.userState.adminForOuTypes
        : null,
      isDemoAccount: state.userState.user.firstName.match(
        /^Demo\s([Dd]irecteur|[Ll]eerkracht)\s[a-zA-Z]\s?/
      ),
      popUpIsOpened: state.userState.popUpIsOpened,
      isKathOndVlaEmployee: false,
    };

    if (state.userState.responsibilities && state.userState.educationLevels) {
      userInfo.manages = {};
      Object.keys(OLD_MIJN_OU_TYPE_CONFIG).forEach((ouType) => {
        userInfo.manages[ouType] = [];
      });
      const resps = state.userState.responsibilities.map((resp) => ({
        ...resp,
        $ou: state.userState.ous[resp.organisationalUnit.href],
        $educationLevel: state.userState.educationLevels[resp.organisationalUnit.href],
      })).filter((resp) => resp.$ou && isAfter(resp.$ou.endDate, getNow()));
      const parentRelations = mapToArray(state.userState.parentRelations.toJS());

      // FOR EACH OU TYPE CONFIG
      // CALCULATE IF THE MANAGING ROLES YOU HAVE FOR THAT TYPE INSIDE MIJN-2.0
      Object.keys(OU_TYPE_CONFIG).forEach((ouType) => {
        if (!OU_TYPE_CONFIG[ouType].childOf) {
          userInfo.manages[ouType] = {};
        }
        const respsOfType = resps.filter(
          (resp) =>
            (resp.$ou.type === ouType ||
              (OU_TYPE_CONFIG[ouType].types && OU_TYPE_CONFIG[ouType].types.some((type) => type === resp.$ou.type) &&
              (!OU_TYPE_CONFIG[ouType].educationLevel || OU_TYPE_CONFIG[ouType].educationLevel === resp.$educationLevel))
            ) &&
            resp.position
        );
        const managers = respsOfType
          .filter((resp) =>
            OU_TYPE_CONFIG[ouType].managers.some(
              (positionHref) => positionHref === resp.position.href
            )
          )
          .map((resp) => ({ responsibility: resp, role: MANAGER }));
        const admins = !OU_TYPE_CONFIG[ouType].admins
          ? []
          : respsOfType
              .filter((resp) =>
                OU_TYPE_CONFIG[ouType].admins.some(
                  (positionHref) =>
                    positionHref === resp.position.href &&
                    !managers.some(
                      (managerResp) =>
                        managerResp.responsibility.organisationalUnit.href === resp.organisationalUnit.href
                    )
                )
              )
              .map((resp) => ({ responsibility: resp, role: DELEGATED_ADMIN }));
        const invoicingAdmins = !OU_TYPE_CONFIG[ouType].invoicingAdmins
          ? []
          : respsOfType
              .filter((resp) =>
                OU_TYPE_CONFIG[ouType].invoicingAdmins.some(
                  (positionHref) =>
                    positionHref === resp.position.href &&
                    !managers.some(
                      (managerResp) =>
                        managerResp.responsibility.organisationalUnit.href === resp.organisationalUnit.href
                    )
                )
              )
              .map((resp) => ({ responsibility: resp, role: INVOICING_ADMIN }));
        const pedagogics = !OU_TYPE_CONFIG[ouType].pedagogicalAdmins
          ? []
          : respsOfType
              .filter((resp) =>
                OU_TYPE_CONFIG[ouType].pedagogicalAdmins.some(
                  (positionHref) =>
                    positionHref === resp.position.href &&
                    !managers.some(
                      (managerResp) =>
                        managerResp.responsibility.organisationalUnit.href === resp.organisationalUnit.href
                    ) &&
                    !admins.some(
                      (managerResp) =>
                        managerResp.responsibility.organisationalUnit.href === resp.organisationalUnit.href
                    )
                )
              )
              .map((resp) => ({ responsibility: resp, role: DELEGATED_PEDAGOGIC }));
        const strategicPurchasers = !OU_TYPE_CONFIG[ouType].strategicPurchasers
          ? []
          : respsOfType
              .filter((resp) =>
                OU_TYPE_CONFIG[ouType].strategicPurchasers.some(
                  (positionHref) =>
                    positionHref === resp.position.href &&
                    !managers.some(
                      (managerResp) =>
                        managerResp.responsibility.organisationalUnit.href === resp.organisationalUnit.href
                    ) &&
                    !admins.some(
                      (managerResp) =>
                        managerResp.responsibility.organisationalUnit.href === resp.organisationalUnit.href
                    ) &&
                    !pedagogics.some(
                      (managerResp) =>
                        managerResp.responsibility.organisationalUnit.href === resp.organisationalUnit.href
                    )
                )
              )
              .map((resp) => ({ responsibility: resp, role: STRATEGIC_PURCHASER }));
        [...managers, ...admins, ...pedagogics, ...strategicPurchasers, ...invoicingAdmins].forEach((obj) => {
          const resp = obj.responsibility;
          if (OU_TYPE_CONFIG[ouType].childOf) {
            const parentRel = parentRelations.find(
              (rel) => rel.from.href === resp.organisationalUnit.href
            );
            const parent = state.userState.ous[parentRel.to.href];
            const educationLevel = state.userState.educationLevels[parent.$$meta.permalink];
            const ouTypeConfigKey = getOuTypeConfigKey(parent.type, educationLevel);
            if (!ouTypeConfigKey) {
              return;
            }
            if (!userInfo.manages[ouTypeConfigKey][parent.key]) {
              userInfo.manages[ouTypeConfigKey][parent.key] = getUserResponsibilityInfo(
                state,
                null,
                parent,
                GROUP_MANAGER,
                null
              );
              userInfo.manages[ouTypeConfigKey][parent.key].groups = [];
            }
            if (userInfo.manages[ouTypeConfigKey][parent.key].groups) {
              userInfo.manages[ouTypeConfigKey][parent.key].groups.push(
                getUserResponsibilityInfo(state, resp, resp.$ou, obj.role)
              );
            }
          } else {
            userInfo.manages[ouType][resp.$ou.key] = getUserResponsibilityInfo(
              state,
              resp,
              resp.$ou,
              obj.role
            );
          }
        });
      });

      // FOR EACH MANAGEMENT ROLE
      Object.keys(userInfo.manages).forEach((ouType) => {
        // ADD MAIL ADDRESS
        userInfo.manages[ouType] = mapToArray(userInfo.manages[ouType]);
          const mailAddresses = mapToArray(state.userState.ouContactDetails.toJS());
          userInfo.manages[ouType].forEach((management) => {
            const ouMailAddress = mailAddresses.find(
              (cd) => cd.organisationalUnit.href === management.ouPermalink && cd.type === 'MAIL'
            );
            if (ouMailAddress) {
              management.mailAddress = ouMailAddress.value;
            }
          });
        // ADD TEACHER GROUPS
        if (ouType === 'SCHOOLENTITY') {
          userInfo.manages[ouType]
            .filter((mgmt) => mgmt.role === GROUP_MANAGER && !mgmt.startDate)
            .forEach((mgmt) => {
              mgmt.startDate = mgmt.groups
                .map((groupMgmt) => groupMgmt.startDate)
                .reduce((acc, cur) => (cur < acc ? cur : acc));
            });
        }
      });
    }
  }

  // CALCULATE PATH TO ROOT
  if (state.userState.dienstParentRelations && state.userState.educationLevels) {
    state.userState.responsibilities.forEach((resp) => {
      const ou = state.userState.ous[resp.organisationalUnit.href];
      if (ou.type !== 'DIENST' && ou.type !== 'EXTERN') {
        return;
      }
      let root = ou;
      let currentTeam = ou;
      while (currentTeam) {
        const parentRel = state.userState.dienstParentRelations.find(
          (rel) => rel.from.href === currentTeam.$$meta.permalink
        );
        if (parentRel) {
          currentTeam = state.userState.ous[parentRel.to.href];
          if (currentTeam) {
            root = currentTeam;
          } else {
            console.warn('[userModel] parentRel.to is not in the state?', parentRel);
            break;
          }
        } else {
          currentTeam = null;
        }
      }
      if (root.$$meta.permalink === KATHOLIEK_ONDERWIJS_VLAANDEREN) {
        userInfo.isKathOndVlaEmployee = true;
      }
    });

    // calculate if there should be a Pop-Up for Pro release and which system the manager is in for delegating
    let oldMijnManager = false;
    let newMijnManager = false;
    Object.keys(userInfo.manages).forEach((ouType) => {
      userInfo.manages[ouType].forEach((mgmt) => {
        if (mgmt.role === 'OLD_MIJN_MANAGER') {
          oldMijnManager = true;
        }
        if (mgmt.canGrantAccessToSensitiveInformation) {
          newMijnManager = true;
        }
      });
    });
    const managingSchoolEntititiesWithoutMailAddress = [...userInfo.manages.PRIMARY_SCHOOLENTITY, ...userInfo.manages.SECONDARY_SCHOOLENTITY].filter(
      (mgmt) => {
        const contactDetails = mapToArray(state.userState.ouContactDetails.toJS()).filter(
          (cd) => cd.organisationalUnit.href === mgmt.ouPermalink
        );
        return (
          (mgmt.role === MANAGER || mgmt.role === DELEGATED_ADMIN) &&
          (!contactDetails.some((cd) => cd.type === 'MAIL') ||
            !contactDetails.some((cd) => cd.type === 'EMAIL') ||
            !contactDetails.some((cd) => cd.type === 'PHONE') ||
            !contactDetails.some((cd) => cd.type === 'WEBSITE'))
        );
      }
    ).map((mgmt) => ({ name: mgmt.name, href: `${mgmt.href}/contactgegevens`, ouKey: mgmt.ouKey }));
    userInfo.missingContactDetailsModal = {
      managingSchoolEntititiesWithoutMailAddress,
      showModal:
        state.userState.ouContactDetailsFetched &&
        managingSchoolEntititiesWithoutMailAddress.length > 0 &&
        !state.userState.userDidSkipContactDetailsModal,
      modalMissingInformationIsOpened: state.userState.modalMissingInformationIsOpened,
    };

    if (oldMijnManager || newMijnManager) {
      // userInfo.popupTitle = '<h1>Nieuw: Toegang tot gevoelige informatie website</h1>';
      userInfo.popupInfo = `
            <h3>Nieuw: Toegang tot gevoelige informatie website</h3>
            <p>Een leidinggevende heeft automatisch toegang tot gevoelige informatie op onze website. Een administratief verantwoordelijke kan via zijn leidinggevende toegang krijgen.</p>
            <p>Daarnaast kun je als leidinggevende of administratief verantwoordelijke nu ook <strong>extra mensen</strong> van het team toegang geven tot die informatie.</p>
            <strong>Hoe?</strong></br>`;
    }
    if (oldMijnManager && newMijnManager) {
      userInfo.popupInfo += `<ul>
          <li><strong>in het basisonderwijs, volwassenenonderwijs, hoger onderwijs, scholengemeenschap of internaat:</strong> geef je teamleden de functie "Raadpleger gevoelige informatie website".</li>
          <li><strong>in het secundair onderwijs, een CLB of een bestuur: </strong>vul bij het veld "Wie krijgt bijkomend toegang tot gevoelige informatie op de website?" de namen van die teamleden in.</li>
        </ul>`;
    } else if (oldMijnManager) {
      userInfo.popupInfo +=
        '<p>geef je teamleden de functie "Raadpleger gevoelige informatie website"</p>';
    } else if (newMijnManager) {
      userInfo.popupInfo +=
        '<p>vul bij het veld "Wie krijgt bijkomend toegang tot gevoelige informatie op de website?" de namen van die teamleden in.</p>';
    }
  }

  return userInfo;
};

export const getUserTabPermission = (tabName, currentUserRole) => {
  let permission = {
    role: currentUserRole,
    permission: FORBIDDEN,
  };

  if (currentUserRole) {
    const tabPermission = POSSIBLE_MANAGEMENT_TABS[tabName].permissions.find(
      (permissionForTab) => permissionForTab.role === currentUserRole
    );
    permission = tabPermission || permission;
  }

  return permission;
};

export const getManagingResponsibilityOfCurrentOrganisationalUnit = (rootState) => {
  if (!rootState.schoolManager.currentOrganisationalUnit) {
    return null;
  }
  const mgmtsOfCurrentType =
    rootState.vm.user.manages[rootState.vm.schoolManager.currentOU.ouTypeConfigKey];
  if (!mgmtsOfCurrentType) {
    return null;
  }
  const currentMgmt = mgmtsOfCurrentType.find(
    (mgmt) => rootState.vm.schoolManager.currentOU.permalink === mgmt.ouPermalink
  );
  if (!currentMgmt) {
    return null;
  }
  const managingRespHref = currentMgmt.permalink;
  const managingResp = rootState.userState.responsibilities.find(
      (resp) => resp.$$meta.permalink === managingRespHref
    );
  return managingResp;
};